import { userPost } from "@/utils/request.js"
import { getForumToken } from './forumSession.js'
import { canUseMeetingSession } from './meetingSessionGuard.js'
import { decodeParam } from "@/utils/utils.js"

const TRACK_URL = "/matrix-search/forum/trackLog/create"
const MINI_TRACK_URL = "/matrix-search/mini/forum/trackLog/create"
const DEFAULT_PLATFORM = "RESEARCH_PC"
const MEETING_MODULE = "activities"
const MEETING_BIZ_TYPE = "meeting"
const CONTEXT_KEY = "meetingTrackContext"
const SCHEMA_VERSION = "1.0"

let trackContext = {}
const onceKeys = new Set()

function createSessionId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function getPageName() {
  const segment = window.location.pathname.split("/").filter(Boolean)[0] || "unknown"
  const pageMap = {
    welcomePage: "welcome",
    appPage: "app_page",
    host: "host",
    visitor: "visitor",
    moderator: "moderator",
    endPage: "end",
  }
  return pageMap[segment] || segment
}

function getClientType() {
  return window.location.pathname.startsWith("/appPage/") ? "app_page" : "web"
}

function getDeviceType() {
  const userAgent = navigator.userAgent || ""
  if (/iPad|Tablet/i.test(userAgent)) return "tablet"
  if (/Android|iPhone|iPod|Mobile|Windows Phone/i.test(userAgent)) return "mobile"
  return "desktop"
}

function storeContext() {
  try {
    sessionStorage.setItem(CONTEXT_KEY, JSON.stringify(trackContext))
  } catch {
    return undefined
  }
}

function loadStoredContext() {
  try {
    const context = JSON.parse(sessionStorage.getItem(CONTEXT_KEY) || "{}")
    if (context && typeof context === "object") return context
  } catch {
    return {}
  }
  return {}
}

function getFallbackContext() {
  try {
    const info = JSON.parse(localStorage.getItem("info") || "{}")
    return {
      bizId: info.forumId || info.id || "",
      meetingInstanceId: info.forumId || info.id || "",
    }
  } catch {
    return {}
  }
}

function ensureTrackContext(context = {}) {
  const stored = loadStoredContext()
  trackContext = {
    ...getFallbackContext(),
    ...stored,
    ...trackContext,
    ...context,
  }
  return trackContext
}

function normalizeEventProperties(eventProperties) {
  if (!eventProperties) return {}
  if (typeof eventProperties === "object") return eventProperties
  try {
    return JSON.parse(eventProperties)
  } catch {
    return {}
  }
}

function buildCommonProperties(context) {
  return {
    schema_version: SCHEMA_VERSION,
    join_session_id: context.joinSessionId || null,
    participant_session_id: context.participantSessionId || null,
    meeting_instance_id: String(context.meetingInstanceId || context.bizId || ""),
    event_sequence_no: context.eventSequenceNo || 0,
    join_role: context.joinRole || null,
    join_role_code: context.joinRoleCode ?? null,
    entry_role: context.entryRole || context.joinRole || null,
    current_role: context.currentRole || context.joinRole || null,
    meeting_phase: context.meetingPhase || "pre_join",
    client_type: context.clientType || getClientType(),
    device_type: context.deviceType || getDeviceType(),
    page_name: context.pageName || getPageName(),
    entry_type: context.entryType || null,
    auth_state: context.authState || "anonymous",
    language: localStorage.getItem("lang") || "zh-CN",
    meeting_status: context.meetingStatus ?? null,
    meeting_status_name: context.meetingStatusName || "unknown",
    is_mini_track: Boolean(context.useMiniTrack),
  }
}

export function getRouteForumId(params) {
  if (!params) return ""

  try {
    const decoded = JSON.parse(decodeParam(params))
    return typeof decoded === "object" ? decoded.forumId || decoded.id || "" : decoded
  } catch {
    return ""
  }
}

export function getMeetingStatusName(status) {
  const statusMap = {
    0: "registering",
    1: "ended",
    3: "waiting",
    4: "in_progress",
    5: "registered",
    6: "rescheduled",
  }
  return statusMap[Number(status)] || "unknown"
}

export function setMeetingTrackContext(context = {}) {
  const previous = ensureTrackContext()
  const nextBizId = context.bizId ?? previous.bizId
  const bizChanged = String(previous.bizId || "") !== String(nextBizId || "")
  trackContext = {
    ...previous,
    ...context,
    bizId: nextBizId,
    meetingInstanceId: context.meetingInstanceId
      || (bizChanged ? nextBizId : previous.meetingInstanceId || nextBizId),
  }

  if (context.meetingStatus !== undefined) {
    trackContext.meetingStatusName = getMeetingStatusName(context.meetingStatus)
  }

  if (bizChanged) {
    trackContext.joinSessionId = ""
    trackContext.participantSessionId = ""
    trackContext.eventSequenceNo = 0
    trackContext.joinStartedAt = 0
    trackContext.joinedAt = 0
    trackContext.meetingPhase = "pre_join"
  }

  storeContext()
}

export function beginMeetingJoinSession(context = {}) {
  const previous = ensureTrackContext()
  const current = { ...previous, ...context }
  const bizChanged = context.bizId !== undefined
    && String(previous.bizId || "") !== String(context.bizId || "")
  const shouldCreate =
    !current.joinSessionId ||
    current.meetingPhase === "ended" ||
    bizChanged

  trackContext = {
    ...current,
    ...context,
    joinSessionId: shouldCreate ? createSessionId() : current.joinSessionId,
    participantSessionId: shouldCreate ? "" : current.participantSessionId || "",
    eventSequenceNo: shouldCreate ? 0 : current.eventSequenceNo || 0,
    joinStartedAt: shouldCreate ? Date.now() : current.joinStartedAt || Date.now(),
    joinedAt: shouldCreate ? 0 : current.joinedAt || 0,
    networkIssueState: shouldCreate ? "" : current.networkIssueState || "",
    meetingPhase: shouldCreate ? "pre_join" : current.meetingPhase || "pre_join",
    entryRole: shouldCreate ? context.joinRole || current.joinRole || null : current.entryRole,
    currentRole: context.currentRole || context.joinRole || current.currentRole || current.joinRole,
  }
  storeContext()
  return trackContext.joinSessionId
}

export function markMeetingJoined(context = {}) {
  const current = ensureTrackContext(context)
  trackContext = {
    ...current,
    ...context,
    participantSessionId: current.participantSessionId || createSessionId(),
    joinedAt: current.joinedAt || Date.now(),
    meetingPhase: "in_meeting",
    entryRole: current.entryRole || context.joinRole || current.joinRole,
    currentRole: context.currentRole || context.joinRole || current.currentRole || current.joinRole,
  }
  storeContext()
  return trackContext.participantSessionId
}

export function completeMeetingSession(context = {}) {
  const current = ensureTrackContext(context)
  trackContext = {
    ...current,
    ...context,
    meetingPhase: "ended",
    endedAt: Date.now(),
  }
  storeContext()
}

export function getMeetingTrackContext() {
  return { ...ensureTrackContext() }
}

export function getMeetingSessionDuration() {
  const context = ensureTrackContext()
  return context.joinedAt ? Math.max(0, Date.now() - context.joinedAt) : 0
}

export function getMeetingOnceKey(eventName, suffix = "") {
  const context = ensureTrackContext()
  return [context.joinSessionId, context.participantSessionId, eventName, suffix]
    .filter(Boolean)
    .join(":")
}

export function trackPoint({
  moduleName,
  eventName,
  bizId,
  bizType,
  eventProperties,
  onceKey,
  useMiniTrack,
} = {}) {
  if (!moduleName || !eventName) return Promise.resolve()
  if (!getForumToken()) return Promise.resolve()
  // Do not attribute the old RTC role/session's events to a newly signed-in account.
  if (!canUseMeetingSession()) return Promise.resolve()
  if (onceKey && onceKeys.has(onceKey)) return Promise.resolve()
  if (onceKey) onceKeys.add(onceKey)

  const context = ensureTrackContext()
  context.eventSequenceNo = (context.eventSequenceNo || 0) + 1
  trackContext = context
  storeContext()

  const isMiniTrack = useMiniTrack ?? context.useMiniTrack
  const properties = {
    ...buildCommonProperties({ ...context, useMiniTrack: isMiniTrack }),
    ...normalizeEventProperties(eventProperties),
  }
  const params = {
    platform: DEFAULT_PLATFORM,
    moduleName,
    eventName,
    bizId: bizId ?? context.bizId ?? "",
    bizType,
    eventTimestamp: Date.now(),
    eventProperties: JSON.stringify(properties),
  }

  return userPost(isMiniTrack ? MINI_TRACK_URL : TRACK_URL, params, {
    skipToken: isMiniTrack,
    sessionCheck: true,
  }).catch(() => {})
}

export function trackMeetingPoint(eventName, options = {}) {
  return trackPoint({
    moduleName: MEETING_MODULE,
    bizType: MEETING_BIZ_TYPE,
    eventName,
    ...options,
  })
}

export function trackMeetingWaitingPoint(eventName, options = {}) {
  return trackMeetingPoint(eventName, options)
}
